import { useState } from "react";
import { toast } from "sonner";
import { useDocumentUploadMutation } from "web_myplann/Api/Document/useDocumentUploadMutation";
import { isNetworkFailure } from "web_myplann/Infrastructure/Api/ApiUtils";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	type AddDocumentSource,
	AddDocumentSources,
} from "web_myplann/Portfolio/Types/AddDocumentSource";
import type { AttachmentPicker } from "web_myplann/Shared/Types/AttachmentPicker";
import { runAttachmentPicker } from "web_myplann/Shared/Utils/runAttachmentPicker";
import { device } from "web_myplann/Utils/device";
import { AttachmentPickStatuses } from "web_myplann/Utils/WebViewModel";

type Args = {
	onUploaded: () => void;
};

export const useAddDocument = ({ onUploaded }: Args) => {
	const { t } = useResource();
	const { mutateAsync: uploadDocument } = useDocumentUploadMutation();
	const [pendingSource, setPendingSource] = useState<AddDocumentSource>();

	const handlePick = async (
		source: AddDocumentSource,
		picker: AttachmentPicker,
	) => {
		if (pendingSource !== undefined) {
			return;
		}

		const picked = await runAttachmentPicker(picker);

		if (picked.status === AttachmentPickStatuses.cancelled) {
			return;
		}

		if (picked.status === AttachmentPickStatuses.error) {
			toast.error(t(Resources.Portfolio.List.AddDocumentDrawer.pickError));
			return;
		}

		setPendingSource(source);

		try {
			await uploadDocument({
				data: { title: picked.filename, file: picked.file },
			});
		} catch (error) {
			if (isNetworkFailure(error)) {
				toast.error(t(Resources.Portfolio.List.AddDocumentDrawer.uploadError));
			}
			return;
		} finally {
			setPendingSource(undefined);
		}

		onUploaded();
	};

	return {
		pendingSource,
		onPickPhoto: () =>
			handlePick(AddDocumentSources.photo, device.pickAttachmentPhoto),
		onPickGallery: () =>
			handlePick(AddDocumentSources.gallery, device.pickAttachmentGallery),
		onPickFile: () =>
			handlePick(AddDocumentSources.file, device.pickAttachmentFile),
	};
};
